export interface LoggerOptions
{
    isEnabled: boolean;
    logLevel: LogLevels;
    separator: string;
    showConsoleColors: boolean;
    showLogLevel: boolean;
    showMethodName: boolean;
    stringifyArguments: boolean;
}

class LogHelper
{
    public static create(options: LoggerOptions)
    {
        const logLevels = Object.keys(LogLevels).map(l => l.toLowerCase());
        const logger: Record<string, any> = {};

        logLevels.forEach((logLevel) =>
        {
            if (logLevels.indexOf(logLevel) >= logLevels.indexOf(options.logLevel) && options.isEnabled)
            {
                logger[logLevel] = (...args: any[]) =>
                {
                    const methodName = LogHelper.getMethodName();
                    const methodNamePrefix = options.showMethodName ? methodName + ` ${options.separator} ` : '';
                    const logLevelPrefix = options.showLogLevel ? logLevel + ` ${options.separator} ` : '';
                    const formattedArguments = options.stringifyArguments ? args.map(a => JSON.stringify(a)) : args;
                    const logMessage = `${logLevelPrefix} ${methodNamePrefix}`;

                    LogHelper.printLogMessage(logLevel, logMessage, options.showConsoleColors, formattedArguments);

                    return `${logMessage} ${formattedArguments.toString()}`;
                };
            }
            else
            {
                logger[logLevel] = (...args: any[]): unknown => undefined;
            }
        }
        );

        return logger as Logger;
    }

    private static getMethodName(): string
    {
        let error = {} as any;

        try
        {
            throw new Error('');
        }
        catch (e)
        {
            error = e;
        }

        // IE9 does not have .stack property
        if (error.stack === undefined)
        {
            return '';
        }

        let stackTrace = error.stack.split('\n')[3];

        if (/ /.test(stackTrace))
        {
            stackTrace = stackTrace.trim().split(' ')[1];
        }

        if (stackTrace && stackTrace.indexOf('.') > -1)
        {
            stackTrace = stackTrace.split('.')[1];
        }

        return stackTrace;
    }

    private static printLogMessage(logLevel: string, logMessage: string, showConsoleColors: boolean, formattedArguments: any)
    {
        if (showConsoleColors && (logLevel === 'warn' || logLevel === 'error' || logLevel === 'fatal'))
        {
            // eslint-disable-next-line no-console
            console[logLevel === 'fatal' ? 'error' : logLevel](logMessage, ...formattedArguments);
        }
        else
        {
            // eslint-disable-next-line no-console
            console.log(logMessage, ...formattedArguments);
        }
    }
}

export default defineNuxtPlugin({
    name: 'logging',
    dependsOn: [],
    async setup(nuxtApp)
    {
        const options: LoggerOptions = {
            logLevel: import.meta.env.PROD ? LogLevels.FATAL : LogLevels.DEBUG,
            isEnabled: true,
            stringifyArguments: false,
            showLogLevel: true,
            showMethodName: false,
            separator: '|',
            showConsoleColors: true
        };
        const logger = LogHelper.create(options);
        const warn = console.warn;

        // Nadpisanie console.warn() aby ukrywać niektóre ostrzeżenia
        console.warn = function (...args)
        {
            if (args[0].includes('definePageMeta()'))
                return;

            warn.apply(console, args);
        };

        return {
            provide: {
                log: logger
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $log: Logger;
    }
}
